import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Mic, MicOff, Loader2, Volume2, HelpCircle } from 'lucide-react';

function detectSpeech() {
  if (typeof window === 'undefined') return { supported: false, Ctor: null };
  const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
  return { supported: !!Ctor, Ctor };
}

/**
 * VoiceCommandButton — always-visible floating mic that lets users navigate
 * the app hands-free with commands like "nueva factura" or "ir a fideicomiso".
 *
 * Behavior:
 *   - Click to start/stop.
 *   - When active, listens continuously and dispatches commands via /api/ai/parse-voice-command.
 *   - Shows the last matched action + hint.
 *   - Persists state per-session (survives navigation, resets on reload).
 *   - Fires window events like 'ezdockit:voice-action' so pages can react to UI-triggers.
 */
export function VoiceCommandButton() {
  const nav = useNavigate();
  const [listening, setListening] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastMatch, setLastMatch] = useState(null);
  const [error, setError] = useState('');
  const [commandsHelp, setCommandsHelp] = useState([]);
  const recRef = useRef(null);
  const shouldStopRef = useRef(false);
  const debounceRef = useRef(null);
  const bufferRef = useRef('');
  const { supported, Ctor } = detectSpeech();

  useEffect(() => {
    // Load command catalog once (cheap, cached)
    api.get('/ai/voice-commands').then(r => setCommandsHelp(r.data?.commands || [])).catch(() => {});
  }, []);

  const handleAction = useCallback((res) => {
    setLastMatch(res);
    if (!res || res.action === 'unknown') {
      toast.error(`No entendí "${res?.transcript || ''}". Diga "buscar", "nueva factura", "ir a fideicomiso"…`, { duration: 3000 });
      return;
    }
    switch (res.action) {
      case 'navigate':
        toast.success(`→ ${res.label}`);
        nav(res.target);
        break;
      case 'open_search':
        toast.success('Abriendo búsqueda…');
        // Simulate Cmd+K
        window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
        break;
      case 'ui_trigger':
        toast.success(`→ ${res.label}`);
        // Pages listen for this and take action
        window.dispatchEvent(new CustomEvent('ezdockit:voice-action', { detail: res }));
        break;
      case 'logout':
        toast.success('Cerrando sesión…');
        window.dispatchEvent(new CustomEvent('ezdockit:voice-action', { detail: res }));
        break;
      default:
        break;
    }
  }, [nav]);

  const parseAndDispatch = useCallback(async (text) => {
    if (!text || text.trim().length < 2) return;
    setBusy(true);
    try {
      const { data } = await api.post('/ai/parse-voice-command', { transcript: text.trim() });
      handleAction(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error interpretando comando');
    } finally { setBusy(false); }
  }, [handleAction]);

  const scheduleParse = useCallback((text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    bufferRef.current = text;
    debounceRef.current = setTimeout(() => {
      const t = bufferRef.current;
      bufferRef.current = '';
      parseAndDispatch(t);
    }, 700);
  }, [parseAndDispatch]);

  const start = useCallback(() => {
    setError('');
    if (!supported) {
      setError('Su navegador no soporta reconocimiento de voz.');
      toast.error('Use Chrome, Edge o Safari para comandos por voz.');
      return;
    }
    try {
      const rec = new Ctor();
      rec.lang = localStorage.getItem('ezdockit.voicecmd.lang') || 'es-PR';
      rec.continuous = true;
      rec.interimResults = true;
      rec.maxAlternatives = 1;
      rec.onresult = (event) => {
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            const txt = event.results[i][0].transcript.trim();
            if (txt) scheduleParse(txt);
          }
        }
      };
      rec.onerror = (e) => {
        const msg = e?.error || '';
        if (msg === 'not-allowed' || msg === 'service-not-allowed') {
          setError('Permiso de micrófono denegado.');
          toast.error('Habilite el micrófono para este sitio.');
          setListening(false);
          shouldStopRef.current = true;
        } else if (msg && msg !== 'no-speech' && msg !== 'aborted') {
          setError(`Error: ${msg}`);
        }
      };
      rec.onend = () => {
        if (!shouldStopRef.current) {
          try { rec.start(); return; } catch (_) { /* ignore */ }
        }
        setListening(false);
      };
      recRef.current = rec;
      shouldStopRef.current = false;
      rec.start();
      setListening(true);
      toast('Escuchando comandos…', { icon: '🎙️' });
    } catch (e) {
      setError(`No se pudo iniciar: ${e?.message || e}`);
    }
  }, [supported, Ctor, scheduleParse]);

  const stop = useCallback(() => {
    shouldStopRef.current = true;
    try { recRef.current?.stop(); } catch (_) { /* ignore */ }
    setListening(false);
  }, []);

  const toggle = () => (listening ? stop() : start());

  return (
    <div className="fixed bottom-4 left-4 z-40 flex items-center gap-2" data-testid="voice-command-widget">
      <div className="relative">
        <Button
          onClick={toggle}
          className={`h-12 w-12 rounded-full shadow-lg border-2 transition-colors ${listening ? 'bg-rose-600 hover:bg-rose-700 border-rose-300 text-white' : 'bg-slate-900 hover:bg-slate-800 border-white text-white'}`}
          size="icon"
          title={listening ? 'Detener comandos por voz' : 'Activar comandos por voz'}
          data-testid="voice-command-toggle"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : listening ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
        </Button>
        {listening && (
          <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-rose-500" />
          </span>
        )}
      </div>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="icon" className="h-8 w-8 rounded-full bg-white shadow-md" data-testid="voice-command-help">
            <HelpCircle className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent side="top" className="w-80" data-testid="voice-command-help-content">
          <div className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <Volume2 className="h-4 w-4 text-emerald-600" /> Comandos por voz
          </div>
          <div className="text-xs text-slate-500 mb-3">
            Diga cualquiera de estas frases (en español o inglés):
          </div>
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {commandsHelp.map(cmd => (
              <div key={cmd.id} className="text-xs" data-testid={`voice-cmd-hint-${cmd.id}`}>
                <span className="font-semibold text-slate-900">{cmd.label}:</span>{' '}
                <span className="text-slate-500">"{cmd.phrases[0]}"</span>
              </div>
            ))}
          </div>
          {lastMatch && (
            <div className="mt-3 pt-3 border-t text-xs text-slate-500">
              <div>Último: <Badge variant="outline" className="ml-1">{lastMatch.label || 'sin match'}</Badge></div>
              <div className="mt-1 italic">"{lastMatch.transcript}"</div>
            </div>
          )}
          {error && <div className="mt-2 text-xs text-rose-700 bg-rose-50 rounded px-2 py-1">{error}</div>}
        </PopoverContent>
      </Popover>
    </div>
  );
}
